import { FormEvent, useRef, useState } from 'react';

import { OpsRotaApiError } from '@/services/opsRota/client';
import type { ManagerUpdateInput, OpsManager } from '@/services/opsRota/types';
import { trackOpsRosterChanged } from '@/util/vexo';

import { useManagerMutations } from '../hooks/useOpsRota';
import { SAVE_FAILED } from '../messages';
import {
  FieldGrid,
  FormButtons,
  PrimaryButton,
  RosterForm,
  TextButton,
  TextField,
} from '../Operations.styles';

import ErrorList from './ErrorList';

// The roster's add / edit form (not designed): first name, last name,
// phone, email. Server errors show inline and the values stay.

type Fields = {
  first_name: string;
  last_name: string;
  phone: string;
  email: string;
};

const BLANK: Fields = { first_name: '', last_name: '', phone: '', email: '' };

const fieldsOf = (m: OpsManager): Fields => ({
  first_name: m.first_name,
  last_name: m.last_name,
  phone: m.phone ?? '',
  email: m.email ?? '',
});

/** Trimmed; blank phone / email are sent as null (graphy stores NULL). */
const clean = (f: Fields) => ({
  first_name: f.first_name.trim(),
  last_name: f.last_name.trim(),
  phone: f.phone.trim() || null,
  email: f.email.trim() || null,
});

/** PATCH only what changed (graphy's update is partial). */
export const changedKeys = (
  original: OpsManager,
  next: Fields,
): ManagerUpdateInput => {
  const c = clean(next);
  const patch: ManagerUpdateInput = {};
  if (c.first_name !== original.first_name) patch.first_name = c.first_name;
  if (c.last_name !== original.last_name) patch.last_name = c.last_name;
  if (c.phone !== (original.phone ?? null)) patch.phone = c.phone;
  if (c.email !== (original.email ?? null)) patch.email = c.email;
  return patch;
};

/** "Email is already on the ops roster" reads as a sentence. */
const sentence = (message: string): string =>
  /[.!?…]$/.test(message.trim()) ? message : `${message.trim()}.`;

export const messagesOf = (err: unknown): string[] =>
  err instanceof OpsRotaApiError ? err.messages.map(sentence) : [SAVE_FAILED];

export type RosterManagerFormProps = {
  /** Set when editing. */
  manager?: OpsManager;
  onCancel: () => void;
  onSaved: () => void;
};

const RosterManagerForm = ({
  manager,
  onCancel,
  onSaved,
}: RosterManagerFormProps) => {
  const [values, setValues] = useState<Fields>(
    manager ? fieldsOf(manager) : BLANK,
  );
  const [error, setError] = useState<string[] | null>(null);
  const inFlight = useRef(false);
  const { create, update } = useManagerMutations();
  const busy = create.isPending || update.isPending;

  const named =
    values.first_name.trim() !== '' && values.last_name.trim() !== '';
  const patch = manager ? changedKeys(manager, values) : null;
  const valid = named && (patch === null || Object.keys(patch).length > 0);

  const set = (key: keyof Fields) => (ev: { target: { value: string } }) =>
    setValues((v) => ({ ...v, [key]: ev.target.value }));

  const submit = async (ev: FormEvent) => {
    ev.preventDefault();
    if (!valid || busy || inFlight.current) return;
    inFlight.current = true;
    setError(null);
    try {
      if (manager && patch) {
        await update.mutateAsync({ id: manager.id, patch });
      } else {
        await create.mutateAsync(clean(values));
      }
      trackOpsRosterChanged({ action: manager ? 'edit' : 'add' });
      onSaved();
    } catch (err) {
      setError(messagesOf(err));
    } finally {
      inFlight.current = false;
    }
  };

  const p = manager ? `opsRosterEdit${manager.id}` : 'opsRosterNew';

  return (
    // HubSpot's collected-forms script (public/index.ejs) reads every <form>
    // submit on the page and posts its fields to HubSpot. A manager's name,
    // phone and email must not leave GravyWork that way. collectedforms.js
    // skips a form only when it carries `hs-do-not-collect` or
    // `data-hs-do-not-collect`; any other name (data-hs-ignore) is not read.
    <RosterForm
      data-hs-do-not-collect="true"
      noValidate
      aria-label={manager ? `Edit ${manager.full_name}` : 'Add manager'}
      aria-busy={busy}
      data-testid="opsRosterForm"
      data-mode={manager ? 'edit' : 'add'}
      onSubmit={submit}
    >
      <FieldGrid>
        <TextField htmlFor={`${p}First`}>
          First name
          <input
            id={`${p}First`}
            value={values.first_name}
            maxLength={128}
            required
            autoComplete="off"
            disabled={busy}
            data-testid="opsRosterFirstName"
            onChange={set('first_name')}
            // Focus the first field when the form opens.
            // eslint-disable-next-line jsx-a11y/no-autofocus
            autoFocus
          />
        </TextField>
        <TextField htmlFor={`${p}Last`}>
          Last name
          <input
            id={`${p}Last`}
            value={values.last_name}
            maxLength={128}
            required
            autoComplete="off"
            disabled={busy}
            data-testid="opsRosterLastName"
            onChange={set('last_name')}
          />
        </TextField>
        <TextField htmlFor={`${p}Phone`}>
          Phone
          <input
            id={`${p}Phone`}
            type="tel"
            value={values.phone}
            maxLength={32}
            placeholder="(555) 555-0100"
            autoComplete="off"
            disabled={busy}
            data-testid="opsRosterPhone"
            onChange={set('phone')}
          />
        </TextField>
        <TextField htmlFor={`${p}Email`}>
          Email
          <input
            id={`${p}Email`}
            type="email"
            value={values.email}
            maxLength={255}
            placeholder="name@example.com"
            autoComplete="off"
            disabled={busy}
            data-testid="opsRosterEmail"
            onChange={set('email')}
          />
        </TextField>
      </FieldGrid>
      {error && (
        <ErrorList messages={error} testId="opsRosterFormError" focusOnShow />
      )}
      <FormButtons>
        <TextButton
          type="button"
          disabled={busy}
          data-testid="opsRosterCancel"
          onClick={onCancel}
        >
          Cancel
        </TextButton>
        <PrimaryButton
          type="submit"
          disabled={!valid || busy}
          data-testid="opsRosterSave"
        >
          {busy ? 'Saving…' : manager ? 'Save changes' : 'Add manager'}
        </PrimaryButton>
      </FormButtons>
    </RosterForm>
  );
};

export default RosterManagerForm;
